/* Client HTTP verso il server (stesso origine tramite proxy /api di Vite).
   I cookie di sessione viaggiano automaticamente (credentials: include). */
import type { DiagramDoc } from "@fullsteps/shared";

export type Role = "admin" | "editor" | "viewer";
export interface User {
  id: number;
  email: string;
  name: string;
  role: Role;
}
export interface VersionMeta {
  id: number;
  createdAt: string;
  author: string | null;
  message: string | null;
}
export interface LockInfo {
  userId: number;
  name: string | null;
  expiresAt: string;
}
export interface ClientSummary {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  diagramCount: number;
}
export interface DiagramSummary {
  id: number;
  clientId: number;
  clientName: string;
  title: string;
  slug: string | null;
  updatedAt: string;
  currentVersionId: number | null;
  lock: LockInfo | null;
  share: ShareInfo;
}
export interface ShareInfo {
  visibility: "private" | "public";
  /** "parte-parlante-token", null finché non si pubblica */
  slug: string | null;
  publishedAt: string | null;
  publishedVersionId: number | null;
  /** il diagramma è stato salvato dopo l'ultima pubblicazione */
  stale: boolean;
}

export interface DiagramState {
  id: number;
  clientId: number;
  title: string;
  updatedAt: string;
  version: VersionMeta | null;
  lock: LockInfo | null;
  share: ShareInfo;
  doc: DiagramDoc | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : `HTTP ${status}`);
  }
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  async me(): Promise<User | null> {
    try {
      return (await req<{ user: User }>("GET", "/api/auth/me")).user;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  login: (email: string, password: string) =>
    req<{ user: User }>("POST", "/api/auth/login", { email, password }).then((r) => r.user),
  logout: () => req<{ ok: true }>("POST", "/api/auth/logout"),

  // clienti
  listClients: () => req<{ clients: ClientSummary[] }>("GET", "/api/clients").then((r) => r.clients),
  createClient: (name: string, slug?: string) =>
    req<{ client: ClientSummary }>("POST", "/api/clients", { name, slug }).then((r) => r.client),

  // diagrammi (catalogo)
  listDiagrams: (clientId?: number) =>
    req<{ diagrams: DiagramSummary[] }>(
      "GET",
      clientId != null ? `/api/diagrams?clientId=${clientId}` : "/api/diagrams",
    ).then((r) => r.diagrams),
  createDiagram: (input: { clientId: number; title: string; doc?: DiagramDoc; fromDiagramId?: number }) =>
    req<{ diagram: { id: number; clientId: number; title: string } }>("POST", "/api/diagrams", input).then(
      (r) => r.diagram,
    ),
  deleteDiagram: (id: number) => req<{ ok: true }>("DELETE", `/api/diagrams/${id}`),

  // diagramma (scoped per id)
  getDiagram: (id: number) => req<DiagramState>("GET", `/api/diagrams/${id}`),
  save: (id: number, doc: DiagramDoc, message: string | undefined, baseVersionId: number | null) =>
    req<{ version: VersionMeta }>("PUT", `/api/diagrams/${id}`, { doc, message, baseVersionId }),
  /* condivisione: l'HTML lo costruisce il client, il server ne conserva la fotografia */
  publish: (id: number, html: string, slug?: string) =>
    req<{ share: ShareInfo }>("POST", `/api/diagrams/${id}/publish`, { html, slug }).then((r) => r.share),
  unpublish: (id: number) =>
    req<{ share: ShareInfo }>("DELETE", `/api/diagrams/${id}/publish`).then((r) => r.share),
  updateShare: (id: number, opts: { slug?: string; regenerate?: boolean }) =>
    req<{ share: ShareInfo }>("PATCH", `/api/diagrams/${id}/share`, opts).then((r) => r.share),

  acquireLock: (id: number) => req<{ lock: LockInfo }>("POST", `/api/diagrams/${id}/lock`),
  releaseLock: (id: number) => req<{ ok: true }>("DELETE", `/api/diagrams/${id}/lock`),

  listVersions: (id: number) =>
    req<{ current: number | null; versions: (VersionMeta & { current: boolean })[] }>(
      "GET",
      `/api/diagrams/${id}/versions`,
    ),
  getVersion: (id: number, vid: number) =>
    req<VersionMeta & { doc: DiagramDoc }>("GET", `/api/diagrams/${id}/versions/${vid}`),
  restoreVersion: (id: number, vid: number) =>
    req<{ version: VersionMeta }>("POST", `/api/diagrams/${id}/versions/${vid}/restore`),
};
